import type { PublicClientApplication } from '@azure/msal-browser';
import type { AccountInfo } from '@azure/msal-browser';
import {
  CacheLookupPolicy,
  InteractionRequiredAuthError,
} from '@azure/msal-browser';

import { SessionError } from './errors';

/**
 * Getting a bearer token for the API. Lives here rather than beside the fetch
 * helpers because both `api.ts` and `api/client.ts` need it, and `api/client.ts`
 * already imports from `api.ts` — putting it in either would close a cycle.
 */
/**
 * Exported so the sign-in request and the silent renewal ask for the same
 * thing. A mismatch between the two makes `acquireTokenSilent` miss the cache
 * and fall through to the redirect path below, on every single call.
 */
export const SCOPES = ['openid', 'profile'];

/**
 * `offline_access` is absent on purpose, not by oversight: msal-common appends
 * `openid profile offline_access` to every request it builds
 * (`RequestParameterBuilder.addScopes`), and strips them again when it looks
 * the result up in the cache (`ScopeSet.createSearchScopes`). Naming it here
 * changes neither the request nor the lookup — the refresh token arrives
 * either way.
 */

/**
 * How much life an ID token must have left to be worth sending.
 *
 * MSAL decides on its own whether to renew, but it decides on the *access*
 * token: `SilentFlowClient.acquireCachedToken` measures `expiresOn` of the
 * cached access token and then returns whatever ID token sits beside it,
 * unexamined. This app sends the ID token to its own API, so it is the one
 * whose expiry matters, and nothing in the silent path is looking at it.
 *
 * Normally the two are issued together and MSAL's five-minute renewal offset
 * covers both, so this never fires. It is there for when they drift — Entra
 * varies access-token lifetimes by a few minutes, a suspended laptop wakes up
 * with a skewed clock, a tab reads a cache another tab refreshed — and the
 * alternative is sending a bearer we can already see is dead and making the
 * API say so.
 */
const MIN_ID_TOKEN_LIFETIME_MS = 60_000;

/**
 * Renew from the cache and the refresh token, and stop there — no hidden iframe.
 *
 * MSAL's last resort, when the refresh token can no longer serve the request, is
 * a hidden iframe sent to `authorize?prompt=none`, riding Entra's own cookie.
 * Three things are wrong with it here.
 *
 * It does not work: msal-browser v5 answers a silent iframe over a
 * `BroadcastChannel`, and the only thing that posts to that channel is
 * `broadcastResponseToMainFrame` from the `redirect-bridge` entry point. Our
 * `redirectUri` is the application itself, so the iframe loads the whole SPA,
 * which calls `handleRedirectPromise` instead and never broadcasts anything.
 * The outer call then waits out `iframeBridgeTimeout` and fails with
 * `timed_out` — ten seconds of LOADING before an error screen, on a session
 * that was merely due for a renewal.
 *
 * It cannot work for much longer either: the iframe reads Entra's cookie in a
 * third-party context, which browsers are busy removing.
 *
 * And it is not needed. `iFrameRenewalPolicies` — Default, Skip and
 * RefreshTokenAndNetwork — is the whole set of policies that reach for it, so
 * naming any other one opts out. A refresh token that is gone or expired then
 * surfaces as the `InteractionRequiredAuthError` it always was, and the catch
 * below already knows what to do with that: redirect, the way this app signs in
 * everywhere else. Entra answers it from its own session without asking the
 * operator anything.
 *
 * `AccessTokenAndRefreshToken` rather than `RefreshToken` so a valid cached
 * access token is still free; `forceRefresh` keeps overriding that on its own,
 * throwing `tokenRefreshRequired` out of the cache and landing on the refresh
 * token, which this policy also permits.
 */
const NO_IFRAME = CacheLookupPolicy.AccessTokenAndRefreshToken;

function expiresTooSoon(claims: { exp?: number } | undefined): boolean {
  const exp = claims?.exp;
  // No `exp` to judge it on. Sending it is better than renewing on every call.
  if (typeof exp !== 'number') {
    return false;
  }
  return exp * 1000 - Date.now() < MIN_ID_TOKEN_LIFETIME_MS;
}

export async function getIdToken(
  instance: PublicClientApplication,
  { forceRefresh = false }: { forceRefresh?: boolean } = {},
): Promise<string> {
  // The active account and nothing else. Falling back to `getAllAccounts()[0]`
  // would reinstate, one layer down, the silent election this app just removed
  // from its bootstrap: a request would go out on behalf of whoever the browser
  // happened to have cached. No account means no session — `_auth`'s beforeLoad
  // has already routed to `/login` by the time this could matter.
  const account = instance.getActiveAccount();
  if (!account) {
    throw new SessionError('Not authenticated');
  }
  try {
    let result = await instance.acquireTokenSilent({
      scopes: SCOPES,
      account,
      forceRefresh,
      cacheLookupPolicy: NO_IFRAME,
    });
    if (!forceRefresh && expiresTooSoon(result.idTokenClaims)) {
      // Ask again, this time telling MSAL not to answer from the cache it just
      // answered from. A refresh token it still holds makes this silent; one it
      // does not lands in the catch below, which is where a dead session
      // belongs.
      result = await instance.acquireTokenSilent({
        scopes: SCOPES,
        account,
        forceRefresh: true,
        cacheLookupPolicy: NO_IFRAME,
      });
    }
    // Holding a token means the last recovery, if there was one, worked. Arming
    // the breaker again is what keeps it from tripping on the next genuine
    // expiry an hour from now.
    clearRecoveryMark();
    return result.idToken;
  } catch (err) {
    // Only an interaction can fix this. A dropped connection or a 5xx from the
    // token endpoint is not that, and treating it as such navigated the whole
    // SPA to Entra: a kiosk operator lost an uncommitted crop, someone lost a
    // filled generation form, and offline the navigation failed too and left
    // them on an Entra error page with no way back. Those callers get the error
    // and can retry.
    if (!(err instanceof InteractionRequiredAuthError)) {
      throw err;
    }

    // The refresh token is gone, or Entra wants an interaction — MFA,
    // conditional access, a revoked session.
    //
    // This used to fall back to `acquireTokenPopup`. That call fires from a
    // route loader or a react-query fetch, so there is no user gesture behind
    // it and browsers block the popup — `getIdToken` then threw a plain Error,
    // which is not an `ApiError`, so the `_auth` loader rethrew it and the
    // operator got an error screen instead of a login page. An expired session
    // is the most ordinary thing that can happen to a long-lived tab.
    //
    // Redirect instead, which is how the app signs in everywhere else.
    return recoverSession(instance, account);
  }
}

/**
 * The one in-flight recovery, shared by every caller that needs it.
 *
 * An expired session is discovered by several requests at once — the `_auth`
 * loader, a poll on a timer, react-query's retry. Each calling
 * `acquireTokenRedirect` means MSAL accepts the first and rejects the rest with
 * `interaction_in_progress`, which is not an `ApiError`, so the `_auth` loader
 * rethrows it into the error screen this whole path exists to remove. Same
 * shape as the token refresh in `@tronche2tek/graph-client`.
 *
 * Deliberately not reset after a failure: if the navigation could not start,
 * every later caller gets that same rejection rather than each firing another
 * attempt. A page load clears it, which is the only state where retrying makes
 * sense anyway.
 */
let recovery: Promise<never> | null = null;

/**
 * The breaker, for the failure `recovery` cannot see.
 *
 * `recovery` dedupes callers within one page load. A redirect loop is made of
 * page loads: we navigate to Entra, come back, the silent call still demands an
 * interaction, and we navigate again — and the module state that would have
 * noticed died with the previous document. Conditional access this browser
 * cannot satisfy, a clock skewed too far for the token to validate, a scope the
 * account will never be granted: none improve on the second attempt, and the
 * operator watches the page flash between two hosts instead of being told
 * anything.
 *
 * So the attempt is recorded where it survives a navigation but not the tab —
 * `sessionStorage`, deliberately *not* the `localStorage` the MSAL cache lives
 * in (`msalConfig.ts`). A loop is a chain of navigations inside one tab, and
 * that is the scope it should be measured and broken in: a tab that tripped the
 * breaker must not lock the operator's other tabs, sharing the same cache, out
 * of signing in. A window rather than a counter because a loop is fast: coming
 * back inside a minute still needing an interaction means the redirect did not
 * help.
 */
const LAST_RECOVERY_KEY = 'auth.lastRecovery';
const RECOVERY_WINDOW_MS = 60_000;

function recoveryJustFailed(): boolean {
  try {
    const at = Number(window.sessionStorage.getItem(LAST_RECOVERY_KEY));
    if (!at) {
      return false;
    }
    const since = Date.now() - at;
    // A clock moved backwards reads as the future. Not a loop — let it through
    // rather than locking the tab out of signing in until the window passes.
    return since >= 0 && since < RECOVERY_WINDOW_MS;
  } catch {
    // Storage disabled. The breaker is gone; signing in is not.
    return false;
  }
}

function markRecoveryAttempt(): void {
  try {
    window.sessionStorage.setItem(LAST_RECOVERY_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
}

function clearRecoveryMark(): void {
  try {
    window.sessionStorage.removeItem(LAST_RECOVERY_KEY);
  } catch {
    /* ignore */
  }
}

function recoverSession(
  instance: PublicClientApplication,
  account: AccountInfo,
): Promise<never> {
  // The in-flight one first. A later caller in this same document is the case
  // `recovery` exists for, not a loop — it must not read the mark its own
  // predecessor just wrote and trip the breaker on it.
  if (recovery) {
    return recovery;
  }
  if (recoveryJustFailed()) {
    // An error screen is a poor outcome and a stable one: it can be read, and
    // reported with something in it. The loop cannot.
    throw new SessionError('Authentication is failing repeatedly');
  }
  // Marked before the call, not after: the navigation may start and this
  // document never run again.
  markRecoveryAttempt();
  recovery = instance
    .acquireTokenRedirect({ scopes: SCOPES, account })
    .then<never>(() => {
      // Reached only if the navigation did not start.
      throw new SessionError('Authentication redirect did not start');
    });
  return recovery;
}

/**
 * Test seam: the in-flight recovery is module state and outlives a test.
 * Clearing it alone is what a page load does — the breaker's mark, in
 * `sessionStorage`, is meant to survive that.
 *
 * @internal
 */
export function __resetRecoveryForTests(): void {
  recovery = null;
}
